import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Activity, Loader2, RefreshCw, User as UserIcon, Ghost } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { adminListActivity, adminActivityMetrics } from '../lib/api';

/**
 * Admin Activity report (`/admin/activity`). Renders three windowed count
 * panels (24h / 7d / 30d) followed by a paginated feed of raw
 * activity_log rows with a small filter bar.
 *
 * The activity_log is the source-of-truth for future admin metric
 * reporting; this page is intentionally thin — heavier dashboards can
 * consume the same `/api/admin/activity` + `/metrics` endpoints later
 * without changing the backend contract.
 */
const EVENT_LABELS = {
  'auth.login': 'Logins',
  'prayer.create': 'Prayers created',
  'prayer.share': 'Shares',
  'prayer.candle.light': 'Candles lit',
  'prayer.candle.unlight': 'Candles unlit',
  'comment.create': 'Comments',
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const MetricsPanel = ({ metrics }) => {
  if (!metrics) return null;
  const windows = [
    { key: 'last_24h', label: 'Last 24 hours' },
    { key: 'last_7d', label: 'Last 7 days' },
    { key: 'last_30d', label: 'Last 30 days' },
  ];
  const eventTypes = metrics.event_types || Object.keys(EVENT_LABELS);
  return (
    <div className="mb-6 space-y-3" data-testid="activity-metrics-panel">
      {windows.map((w) => {
        const bucket = metrics[w.key] || {};
        const total = bucket._total || 0;
        return (
          <div
            key={w.key}
            data-testid={`metrics-row-${w.key}`}
            className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4"
          >
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">{w.label}</div>
              <div
                className="text-2xl font-bold tabular-nums text-amber-600 dark:text-amber-400"
                data-testid={`metrics-total-${w.key}`}
              >
                {total}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              {eventTypes.map((et) => (
                <div
                  key={et}
                  data-testid={`metrics-cell-${w.key}-${et}`}
                  className="flex items-baseline justify-between px-2 py-1 rounded bg-zinc-50 dark:bg-zinc-800/60"
                >
                  <span className="text-zinc-600 dark:text-zinc-300 truncate">{EVENT_LABELS[et] || et}</span>
                  <span className="font-semibold tabular-nums ml-2">{bucket[et] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AdminActivity = () => {
  const { user, loading: authLoading } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [eventType, setEventType] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [m, feed] = await Promise.all([
        adminActivityMetrics(),
        adminListActivity({
          limit: 100,
          ...(eventType ? { event_type: eventType } : {}),
          ...(userIdFilter ? { user_id: userIdFilter.trim() } : {}),
        }),
      ]);
      setMetrics(m);
      setRows(Array.isArray(feed?.items) ? feed.items : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (user?.role === 'admin') load();
  }, [user, eventType, userIdFilter]);

  const eventTypesForFilter = useMemo(() => (
    metrics?.event_types || Object.keys(EVENT_LABELS)
  ), [metrics]);

  if (authLoading) {
    return <div className="max-w-5xl mx-auto p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline" /> Loading…</div>;
  }
  if (!user) {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <p className="text-zinc-500 mb-4">Please sign in to view this page.</p>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }
  if (user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Admin only</h1>
        <p className="text-zinc-500 mb-4">This page is restricted to moderators.</p>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-6">
        <Activity size={22} className="text-amber-500" />
        <h1 className="text-2xl font-bold" data-testid="admin-activity-title">Admin · Activity</h1>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="refresh-activity-btn"
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      <MetricsPanel metrics={metrics} />

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <select
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          data-testid="filter-event-type"
          className="h-9 px-3 rounded-full border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
        >
          <option value="">All event types</option>
          {eventTypesForFilter.map((et) => (
            <option key={et} value={et}>{EVENT_LABELS[et] || et}</option>
          ))}
        </select>
        <input
          type="text"
          value={userIdFilter}
          onChange={(e) => setUserIdFilter(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
          data-testid="filter-user-id"
          placeholder="user_id (or 'null' for anon)"
          className="h-9 px-3 rounded-full border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm w-72"
        />
        <div className="text-xs text-zinc-500 ml-1" data-testid="activity-count-label">
          {rows.length} recent events
        </div>
      </div>

      {err && (
        <div
          className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm mb-4"
          data-testid="admin-activity-error"
        >
          {err}
        </div>
      )}

      <div className="overflow-x-auto -mx-4 sm:mx-0 rounded-xl border border-zinc-200 dark:border-zinc-800">
        <table className="w-full text-sm min-w-[720px]" data-testid="admin-activity-table">
          <thead className="bg-zinc-50 dark:bg-zinc-900/60 text-left">
            <tr className="text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 font-semibold">When</th>
              <th className="px-4 py-3 font-semibold">Event</th>
              <th className="px-4 py-3 font-semibold">Actor</th>
              <th className="px-4 py-3 font-semibold">Meta</th>
              <th className="px-4 py-3 font-semibold">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-zinc-500">
                  <Loader2 className="animate-spin inline mr-2" size={14} /> Loading activity…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-zinc-500" data-testid="admin-activity-empty">
                  No events match your filters.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`admin-activity-row-${r.id}`}
                  className="hover:bg-zinc-50 dark:hover:bg-zinc-900/40 align-top"
                >
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400 tabular-nums whitespace-nowrap">
                    {fmtDateTime(r.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">
                      {EVENT_LABELS[r.event_type] || r.event_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300 whitespace-nowrap">
                    {r.user_id ? (
                      <span className="inline-flex items-center gap-1.5">
                        <UserIcon size={13} className="text-zinc-500" />
                        <span className="font-medium">@{r.user_handle || r.user_id}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-zinc-500 italic">
                        <Ghost size={13} /> anon
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <code
                      className="text-[11px] text-zinc-600 dark:text-zinc-400 break-all"
                      data-testid={`activity-meta-${r.id}`}
                    >
                      {r.meta && Object.keys(r.meta).length ? JSON.stringify(r.meta) : '—'}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 tabular-nums whitespace-nowrap">
                    {r.ip || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
        <Link
          to="/admin/reports"
          data-testid="admin-reports-link"
          className="underline hover:text-zinc-800 dark:hover:text-zinc-200"
        >
          Admin · Reports
        </Link>
        <Link
          to="/admin/users"
          data-testid="admin-users-link"
          className="underline hover:text-zinc-800 dark:hover:text-zinc-200"
        >
          Admin · Users
        </Link>
        <span>Data source for future metric reporting: <code>activity_log</code> collection</span>
      </div>
    </div>
  );
};

export default AdminActivity;
